import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  Image,
  Badge,
  Button,
  Modal,
  Table,
  Accordion,
  ListGroup
} from 'react-bootstrap';
import { 
  FaGraduationCap,
  FaCalendarAlt,
  FaFileAlt,
  FaCheckCircle,
  FaClock,
  FaSearch,
  FaListAlt,
  FaUserGraduate,
  FaCertificate,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaArrowRight,
  FaDownload,
  FaInfoCircle,
  FaQuestionCircle,
  FaRegCalendarCheck,
  FaBook,
  FaPenAlt,
  FaChalkboardTeacher,
  FaUniversity,
  FaAward,
  FaHandshake,
  FaChartLine,
  FaSync  // This was missing
} from 'react-icons/fa';
import { BASE_URL } from '../../services/api';

// Program icons outside component
const PROGRAM_ICONS = {
  'engineering': <FaUniversity />,
  'medical': <FaAward />,
  'science': <FaGraduationCap />,
  'arts': <FaBook />,
  'commerce': <FaChartLine />,
  'law': <FaHandshake />,
  'management': <FaChalkboardTeacher />,
  'technology': <FaCertificate />,
  'pharmacy': <FaUniversity />,
  'architecture': <FaUniversity />,
  'default': <FaGraduationCap />
};

const AdmissionProcedureDetails = () => {
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Color schemes for different program types
  const programColors = {
    'engineering': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'medical': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'science': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'arts': 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'commerce': 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'law': 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'management': 'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)',
    'technology': 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
    'default': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  };

  // Helper functions wrapped in useCallback
  const getShortDescription = useCallback((description) => {
    if (!description) return '';
    const firstPara = description.split('\n\n')[0];
    return firstPara.length > 120 ? firstPara.substring(0, 120) + '...' : firstPara;
  }, []);

  const getProgramType = useCallback((programName) => {
    const lowerName = programName.toLowerCase();
    const types = Object.keys(PROGRAM_ICONS);
    for (const type of types) {
      if (lowerName.includes(type)) return type;
    }
    return 'default';
  }, []);

  const getProgramIcon = useCallback((programName) => {
    const programType = getProgramType(programName);
    return PROGRAM_ICONS[programType] || PROGRAM_ICONS.default;
  }, [getProgramType]);

  const getProgramColor = useCallback((programName) => {
    const programType = getProgramType(programName);
    return programColors[programType] || programColors.default;
  }, [getProgramType]);

  const extractImportantDates = useCallback((datesText) => {
    if (!datesText) return [];
    const lines = datesText.split('\n');
    return lines.filter(line => line.trim() !== '').slice(0, 5);
  }, []);

  const extractEligibilityPoints = useCallback((eligibilityText) => {
    if (!eligibilityText) return [];
    const points = eligibilityText.split(/[\n•\-]/).filter(point => point.trim() !== '');
    return points.slice(0, 6);
  }, []);

  const extractDocuments = useCallback((documentsText) => {
    if (!documentsText) return [];
    const documents = documentsText.split(/[\n•\-]/).filter(doc => doc.trim() !== '');
    return documents.slice(0, 8);
  }, []);

  // Fetch admission procedures
  const fetchProcedures = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${BASE_URL}/admission_procedure.php?action=getAll`);
      const result = await response.json();

      if (result.success && Array.isArray(result.data) && result.data.length > 0) {
        const enhancedProcedures = result.data.map((procedure, index) => ({
          ...procedure,
          programType: getProgramType(procedure.program_name),
          shortDescription: getShortDescription(procedure.procedure_details),
          importantDatesList: extractImportantDates(procedure.important_dates),
          eligibilityPoints: extractEligibilityPoints(procedure.eligibility_criteria),
          requiredDocuments: extractDocuments(procedure.required_documents),
          lastUpdated: new Date(procedure.updated_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })
        }));
        setProcedures(enhancedProcedures);
      } else {
        setError('No admission procedures available');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load admission procedures');
    } finally {
      setLoading(false);
    }
  }, [getProgramType, getShortDescription, extractImportantDates, extractEligibilityPoints, extractDocuments]);

  useEffect(() => {
    fetchProcedures();
  }, [fetchProcedures]);

  const handleViewDetails = (procedure) => {
    setSelectedProcedure(procedure);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedProcedure(null);
    setShowModal(false);
  };

  const filteredProcedures = procedures.filter(procedure => {
    const matchesSearch = procedure.program_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (procedure.shortDescription && procedure.shortDescription.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filterType === 'all' || procedure.programType === filterType;
    return matchesSearch && matchesFilter;
  });

  const programTypes = ['all', ...new Set(procedures.map(p => p.programType))];

  if (loading) {
    return (
      <Container className="admission-container py-5">
        <div className="text-center">
          <div className="loading-animation">
            <div className="loading-dots">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
          <h4 className="mt-4 text-gradient">Loading Admission Procedures</h4>
          <p className="text-muted">Fetching program details...</p>
        </div>
      </Container>
    );
  }

  if (error || procedures.length === 0) {
    return (
      <Container className="py-5">
        <Alert variant="warning" className="border-0 shadow-sm">
          <Alert.Heading>
            <FaInfoCircle className="me-2" />
            Admission Information Not Available
          </Alert.Heading>
          <p>{error || 'No admission procedures found.'}</p>
          <Button variant="primary" onClick={fetchProcedures}>
            <FaSync className="me-2" />
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <div className="admission-portal">
      {/* Hero Section */}
      <div className="admission-hero">
        <Container className="py-5">
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              {/* <Badge bg="light" text="dark" className="hero-badge mb-3">
                <FaGraduationCap className="me-2" /> Admission 2024
              </Badge> */}
              <h1 className="display-5 fw-bold text-white mb-3">
                Admission <span className="text-gradient">Procedure</span>
              </h1>
              <p className="lead text-white-50 mb-4">
                Discover comprehensive admission procedures, eligibility criteria, 
                and important dates for all our programs.
              </p>
              <div className="hero-stats">
                <div className="stat-item">
                  <h3 className="text-white mb-0">{procedures.length}+</h3>
                  <small className="text-white-50">Programs</small>
                </div>
                <div className="stat-item">
                  <h3 className="text-white mb-0">100%</h3>
                  <small className="text-white-50">Transparency</small>
                </div>
                <div className="stat-item">
                  <h3 className="text-white mb-0">24/7</h3>
                  <small className="text-white-50">Guidance</small>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="hero-visual">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <div className="floating-shape shape-3"></div>
                <div className="admission-preview-card">
                  <div className="preview-icon">
                    <FaRegCalendarCheck />
                  </div>
                  <h5 className="mb-2">Admission Timeline</h5>
                  <p className="small mb-0">Stay updated with important dates</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-5">
        {/* Search and Filter Section */}
        <div className="search-filter-section mb-5">
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="search-wrapper">
                <div className="search-box">
                  <div className="search-icon">
                    <FaSearch />
                  </div>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search programs or details..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <Button 
                      variant="link" 
                      className="search-clear"
                      onClick={() => setSearchTerm('')}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="filter-wrapper">
                <div className="filter-badges">
                  <Badge 
                    bg={filterType === 'all' ? 'primary' : 'light'} 
                    text={filterType === 'all' ? 'white' : 'dark'}
                    className="filter-badge me-2 mb-2"
                    onClick={() => setFilterType('all')}
                    style={{ cursor: 'pointer' }}
                  >
                    All Programs
                  </Badge>
                  {programTypes.filter(type => type !== 'all').map((type, index) => (
                    <Badge 
                      key={index}
                      bg={filterType === type ? 'primary' : 'light'} 
                      text={filterType === type ? 'white' : 'dark'}
                      className="filter-badge me-2 mb-2"
                      onClick={() => setFilterType(type)}
                      style={{ cursor: 'pointer' }}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </Badge>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Programs Grid */}
        <Row className="g-4">
          {filteredProcedures.map((procedure, index) => (
            <Col lg={4} md={6} key={procedure.id}>
              <div className="program-card-wrapper">
                <Card className="program-card border-0 shadow-lg">
                  <div 
                    className="program-card-header"
                    style={{ background: getProgramColor(procedure.program_name) }}
                  >
                    <div className="program-icon-wrapper">
                      <div className="program-icon">
                        {getProgramIcon(procedure.program_name)}
                      </div>
                      <Badge bg="light" text="dark" className="program-type-badge">
                        {procedure.programType}
                      </Badge>
                    </div>
                  </div>
                  <Card.Body className="p-4">
                    <h4 className="program-title mb-3">{procedure.program_name}</h4>
                    
                    <p className="program-description text-muted mb-3">
                      {procedure.shortDescription}
                    </p>
                    
                    <div className="program-dates mb-3">
                      <h6 className="text-primary mb-2">
                        <FaCalendarAlt className="me-2" />
                        Important Dates
                      </h6>
                      <ul className="list-unstyled mb-0">
                        {procedure.importantDatesList.slice(0, 2).map((date, idx) => (
                          <li key={idx} className="small text-muted mb-1">
                            • {date}
                          </li>
                        ))}
                        {procedure.importantDatesList.length > 2 && (
                          <li className="small text-muted">
                            • +{procedure.importantDatesList.length - 2} more dates
                          </li>
                        )}
                      </ul>
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div className="program-stats">
                        <div className="stat">
                          <FaClock className="stat-icon" />
                          <span className="stat-label">Updated: {procedure.lastUpdated}</span>
                        </div>
                      </div>
                      <Button
                        variant="primary"
                        className="program-action-btn"
                        onClick={() => handleViewDetails(procedure)}
                      >
                        View Details
                        <FaArrowRight className="ms-2" />
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </Col>
          ))}
        </Row>

        {/* No Results Message */}
        {filteredProcedures.length === 0 && (
          <div className="no-results text-center py-5">
            <div className="no-results-icon mb-4">
              <FaSearch size={60} className="text-muted" />
            </div>
            <h4 className="mb-3">No Programs Found</h4>
            <p className="text-muted mb-4">Try a different search term or filter</p>
            <Button 
              variant="outline-primary" 
              onClick={() => {
                setSearchTerm('');
                setFilterType('all');
              }}
            >
              Show All Programs
            </Button>
          </div>
        )}

        {/* Quick Guide Section */}
        <Row className="mt-5 pt-5">
          <Col lg={12}>
            <div className="section-header text-center mb-5">
              <Badge bg="light" text="dark" className="mb-3 px-3 py-2">
                <FaInfoCircle className="me-2" /> Admission Guide
              </Badge>
              <h2 className="mb-3">Quick Admission Process</h2>
              <p className="text-muted">Follow these simple steps for successful admission</p>
            </div>
            
            <div className="process-steps">
              <Row className="g-4">
                <Col lg={3} md={6}>
                  <div className="step-card">
                    <div className="step-number">1</div>
                    <div className="step-icon">
                      <FaFileAlt />
                    </div>
                    <h5>Check Eligibility</h5>
                    <p className="text-muted small">Review program-specific eligibility criteria</p>
                  </div>
                </Col>
                <Col lg={3} md={6}>
                  <div className="step-card">
                    <div className="step-number">2</div>
                    <div className="step-icon">
                      <FaPenAlt />
                    </div>
                    <h5>Apply   </h5>
                    <p className="text-muted small">Submit application with required documents</p>
                  </div>
                </Col>
                <Col lg={3} md={6}>
                  <div className="step-card">
                    <div className="step-number">3</div>
                    <div className="step-icon">
                      <FaCheckCircle />
                    </div>
                    <h5>Verification</h5>
                    <p className="text-muted small">Document verification and processing</p>
                  </div>
                </Col>
                <Col lg={3} md={6}>
                  <div className="step-card">
                    <div className="step-number">4</div>
                    <div className="step-icon">
                      <FaUserGraduate />
                    </div>
                    <h5>Enrollment</h5>
                    <p className="text-muted small">Complete enrollment and join classes</p>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Program Detail Modal */}
      {selectedProcedure && (
        <Modal 
          show={showModal} 
          onHide={handleCloseModal} 
          centered 
          size="xl"
          className="program-detail-modal"
        >
          <Modal.Header closeButton className="border-0">
            <Modal.Title>
              <div className="d-flex align-items-center">
                <div 
                  className="modal-program-icon me-3"
                  style={{ background: getProgramColor(selectedProcedure.program_name) }}
                >
                  {getProgramIcon(selectedProcedure.program_name)}
                </div>
                <div>
                  <h3 className="mb-0">{selectedProcedure.program_name}</h3>
                  <Badge bg="light" text="dark" className="mt-2">
                    {selectedProcedure.programType.toUpperCase()}
                  </Badge>
                </div>
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Row className="mb-4">
              <Col lg={8}>
                <div className="program-info">
                  <h5 className="mb-3 text-primary">
                    <FaInfoCircle className="me-2" />
                    Program Overview
                  </h5>
                  <div className="program-description-full">
                    {selectedProcedure.procedure_details.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="mb-3">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </Col>
              <Col lg={4}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <h6 className="text-primary mb-3">
                      <FaClock className="me-2" />
                      Important Dates
                    </h6>
                    <ListGroup variant="flush">
                      {selectedProcedure.importantDatesList.map((date, index) => (
                        <ListGroup.Item key={index} className="d-flex align-items-center">
                          <FaCalendarAlt className="text-primary me-2" />
                          <span>{date}</span>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-4">
              <Col lg={6}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    <h5 className="text-primary mb-3">
                      <FaCheckCircle className="me-2" />
                      Eligibility Criteria
                    </h5>
                    <ListGroup variant="flush">
                      {selectedProcedure.eligibilityPoints.map((point, index) => (
                        <ListGroup.Item key={index} className="d-flex align-items-start">
                          <div className="me-2">
                            <FaCheckCircle className="text-success mt-1" />
                          </div>
                          <span>{point}</span>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col lg={6}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    <h5 className="text-primary mb-3">
                      <FaListAlt className="me-2" />
                      Required Documents
                    </h5>
                    <ListGroup variant="flush">
                      {selectedProcedure.requiredDocuments.map((doc, index) => (
                        <ListGroup.Item key={index} className="d-flex align-items-start">
                          <div className="me-2">
                            <FaFileAlt className="text-primary mt-1" />
                          </div>
                          <span>{doc}</span>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {selectedProcedure.contact_info && (
              <Card className="border-0 shadow-sm mt-4">
                <Card.Body>
                  <h5 className="text-primary mb-3">
                    <FaPhoneAlt className="me-2" />
                    Contact Information
                  </h5>
                  <div className="contact-details">
                    <p>{selectedProcedure.contact_info}</p>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="outline-secondary" onClick={handleCloseModal}>
              Close
            </Button>
            {/* <Button variant="primary">
              <FaDownload className="me-2" />
              Download Brochure
            </Button> */}
            <Button variant="success">
              <FaPenAlt className="me-2" />
              Apply Now
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <style>{`
        .admission-portal {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          min-height: 100vh;
        }
        
        .admission-hero {
          background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
          position: relative;
          overflow: hidden;
        }
        
        .admission-hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E");
          opacity: 0.3;
        }
        
        .hero-badge {
          font-size: 0.9rem;
          padding: 8px 16px;
          border-radius: 20px;
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
        
        .text-gradient {
          background: linear-gradient(90deg, #3498db, #2ecc71);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .hero-stats {
          display: flex;
          gap: 30px;
          margin-top: 30px;
        }
        
        .stat-item {
          display: flex;
          flex-direction: column;
        }
        
        .hero-visual {
          position: relative;
          height: 300px;
        }
        
        .floating-shape {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          animation: float 6s ease-in-out infinite;
        }
        
        .shape-1 {
          width: 100px;
          height: 100px;
          top: 20px;
          right: 50px;
          animation-delay: 0s;
        }
        
        .shape-2 {
          width: 70px;
          height: 70px;
          bottom: 40px;
          right: 100px;
          animation-delay: 2s;
        }
        
        .shape-3 {
          width: 50px;
          height: 50px;
          top: 100px;
          right: 150px;
          animation-delay: 4s;
        }
        
        .admission-preview-card {
          position: absolute;
          top: 50%;
          left: 0;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.95);
          padding: 20px;
          border-radius: 15px;
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
          width: 200px;
        }
        
        .preview-icon {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #3498db, #2ecc71);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
          margin-bottom: 15px;
        }
        
        .search-filter-section {
          background: white;
          padding: 25px;
          border-radius: 15px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
        }
        
        .search-wrapper {
          position: relative;
        }
        
        .search-box {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .search-icon {
          position: absolute;
          left: 20px;
          color: #6c757d;
          z-index: 2;
        }
        
        .search-input {
          width: 100%;
          padding: 15px 20px 15px 50px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: white;
          padding-right: 100px;
        }
        
        .search-input:focus {
          outline: none;
          border-color: #3498db;
          box-shadow: 0 5px 15px rgba(52, 152, 219, 0.1);
        }
        
        .search-clear {
          position: absolute;
          right: 10px;
          padding: 5px 10px;
          font-size: 0.875rem;
          text-decoration: none;
        }
        
        .filter-badges {
          display: flex;
          flex-wrap: wrap;
        }
        
        .filter-badge {
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.875rem;
          transition: all 0.3s ease;
        }
        
        .filter-badge:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
        }
        
        .program-card-wrapper {
          position: relative;
          height: 100%;
        }
        
        .program-card {
          border-radius: 16px;
          overflow: hidden;
          transition: all 0.4s ease;
          height: 100%;
        }
        
        .program-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
        }
        
        .program-card-header {
          height: 120px;
          position: relative;
          padding: 20px;
        }
        
        .program-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        
        .program-icon {
          width: 60px;
          height: 60px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          color: white;
          backdrop-filter: blur(10px);
        }
        
        .program-type-badge {
          font-size: 0.75rem;
          padding: 6px 12px;
          border-radius: 20px;
          text-transform: uppercase;
          font-weight: 600;
        }
        
        .program-title {
          font-weight: 600;
          color: #2c3e50;
        }
        
        .program-description {
          line-height: 1.6;
          min-height: 60px;
          font-size: 0.95rem;
        }
        
        .program-dates {
          min-height: 80px;
        }
        
        .program-stats {
          display: flex;
          align-items: center;
        }
        
        .stat {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .stat-icon {
          font-size: 16px;
          color: #3498db;
        }
        
        .stat-label {
          font-size: 0.8rem;
          color: #6c757d;
        }
        
        .program-action-btn {
          padding: 8px 20px;
          border-radius: 8px;
          font-weight: 500;
          display: flex;
          align-items: center;
        }
        
        .loading-animation {
          display: flex;
          justify-content: center;
        }
        
        .loading-dots {
          display: flex;
          gap: 10px;
        }
        
        .dot {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3498db, #2ecc71);
          animation: bounce 1.4s infinite ease-in-out;
        }
        
        .dot:nth-child(1) { animation-delay: -0.32s; }
        .dot:nth-child(2) { animation-delay: -0.16s; }
        
        .process-steps {
          padding: 0 20px;
        }
        
        .step-card {
          background: white;
          padding: 30px;
          border-radius: 15px;
          text-align: center;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
          height: 100%;
          position: relative;
        }
        
        .step-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
        }
        
        .step-number {
          position: absolute;
          top: -15px;
          left: -15px;
          width: 40px;
          height: 40px;
          background: #3498db;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 1.2rem;
        }
        
        .step-icon {
          width: 70px;
          height: 70px;
          margin: 0 auto 20px;
          background: linear-gradient(135deg, #3498db, #2ecc71);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 28px;
        }
        
        .modal-program-icon {
          width: 60px;
          height: 60px;
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 28px;
        }
        
        .program-description-full {
          line-height: 1.8;
          color: #333;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
        
        @media (max-width: 768px) {
          .hero-stats {
            flex-direction: column;
            gap: 15px;
          }
          
          .search-filter-section {
            padding: 15px;
          }
          
          .step-card {
            padding: 20px;
          }
          
          .modal-program-icon {
            width: 50px;
            height: 50px;
            font-size: 24px;
          }
        }
        
        @media (max-width: 576px) {
          .hero-visual {
            height: 200px;
          }
          
          .admission-preview-card {
            width: 150px;
            padding: 15px;
          }
          
          .filter-badge {
            padding: 6px 12px;
            font-size: 0.8rem;
          }
        }
      `}</style>
    </div>
  );
};

export default AdmissionProcedureDetails;